// src/pages/User/components/UserCardEnhanced.jsx - Complete Integration
import React, { useState, memo, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/common/Button";
import Checkbox from "../../../components/common/Checkbox";
import Badge from "../../../components/common/Badge";
import ActionButtons from "../../../components/PageUI/ActionButtons";
import {
  userButtonsConfig,
  userStatusVariants,
  statusMessages,
  userFilterStatus,
} from "../constants/userConstants";
import UserModal from "./UserModal";
import UserAnalyticsDashboard from "./UserAnalyticsDashboard";
import CustomerProfileManager from "./CustomerProfileManager";
import LoyaltyManager from "./LoyaltyManager";
import UserBranchManager from "./UserBranchManager";
import {
  AppointmentPreferencesModal,
  CommunicationPreferencesModal,
} from "./PreferencesModals";
import {
  getUsersForAdminThunk,
  updateUserByAdminThunk,
} from "../../../store/thunks/userThunks";
import {
  UserIcon,
  CalendarIcon,
  ChevronDownIcon,
  MailIcon,
  PhoneIcon,
  LocationMarkerIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  OfficeBuildingIcon,
  ChatAlt2Icon,
  ChartBarIcon,
  StarIcon,
  TrendingUpIcon,
  CheckCircleIcon,
  DotsVerticalIcon,
} from "@heroicons/react/outline";

const UserCardEnhanced = ({
  user,
  handleSelectUser,
  handleSingleAction,
  selected,
  currentFilter,
  onEditAdminMessage,
  expandedByDefault = false,
  showAnalytics = true,
}) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.user);

  // State management - All modals
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isLoyaltyOpen, setIsLoyaltyOpen] = useState(false);
  const [isBranchOpen, setIsBranchOpen] = useState(false);
  const [isApptPrefOpen, setIsApptPrefOpen] = useState(false);
  const [isCommPrefOpen, setIsCommPrefOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(expandedByDefault);
  const [showQuickActions, setShowQuickActions] = useState(false);

  React.useEffect(() => {
    setIsExpanded(expandedByDefault);
  }, [expandedByDefault]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const openEditModal = () => {
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleEditUser = async (updatedUserData) => {
    const action = await dispatch(
      updateUserByAdminThunk({
        userId: user._id,
        userData: updatedUserData,
      })
    );
    if (updateUserByAdminThunk.fulfilled.match(action)) {
      dispatch(
        getUsersForAdminThunk({
          status: userFilterStatus[currentFilter],
          includeUnverified: currentFilter === "Unverified",
        })
      );
      closeEditModal();
      return null;
    } else {
      return action.payload || "Failed to update customer";
    }
  };

  const handleAction = (action) => {
    setShowQuickActions(false); // Close dropdown

    switch (action) {
      case "edit":
        openEditModal();
        break;
      case "analytics":
        setIsAnalyticsOpen(true);
        break;
      case "profile":
        setIsProfileOpen(true);
        break;
      case "loyalty":
        setIsLoyaltyOpen(true);
        break;
      case "branch":
        setIsBranchOpen(true);
        break;
      case "apptPref":
        setIsApptPrefOpen(true);
        break;
      case "commPref":
        setIsCommPrefOpen(true);
        break;
      default:
        handleSingleAction(user._id, action);
    }
  };

  const getButtonsConfig = () => {
    const config =
      userButtonsConfig[currentFilter] || userButtonsConfig.default;
    let buttons = config;

    if (showAnalytics && user.status === "accepted") {
      buttons = [
        {
          variant: "info-outline",
          action: "analytics",
          label: "Analytics",
          icon: <ChartBarIcon className="w-4 h-4" />,
        },
        ...buttons.filter(
          (b) =>
            !["analytics", "profile", "loyalty", "branch"].includes(b.action)
        ),
      ];
    }

    if (!user.createdByAdmin) {
      return buttons.filter((button) => button.action !== "edit");
    }
    return buttons;
  };

  const formattedDate = new Date(user.createdAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const memoizedUser = useMemo(() => user, [user._id]);

  const hasAdminMessage =
    user.status === "blocked" ||
    user.status === "rejected" ||
    user.status === "admin-created";

  const quickStats = useMemo(() => {
    const stats = {
      appointments: user.visitHistory?.global?.totalAppointments || 0,
      showRate: user.visitHistory?.global?.appointmentShowRate || 0,
      totalSpent: user.visitHistory?.global?.totalSpent || 0,
      loyaltyPoints: user.loyalty?.points || 0,
      membershipTier: user.loyalty?.membershipTier || "REGULAR",
    };
    return stats;
  }, [user]);

  // Quick actions menu items
  const quickActionsMenu = [
    {
      label: "View Analytics",
      action: "analytics",
      icon: <ChartBarIcon className="w-4 h-4" />,
      show: showAnalytics && user.status === "accepted",
    },
    {
      label: "Customer Profile",
      action: "profile",
      icon: <UserIcon className="w-4 h-4" />,
      show: user.status === "accepted",
    },
    {
      label: "Loyalty Management",
      action: "loyalty",
      icon: <StarIcon className="w-4 h-4" />,
      show: user.status === "accepted",
    },
    {
      label: "Branch Management",
      action: "branch",
      icon: <OfficeBuildingIcon className="w-4 h-4" />,
      show: user.status === "accepted",
    },
    {
      label: "Appointment Preferences",
      action: "apptPref",
      icon: <CalendarIcon className="w-4 h-4" />,
      show: user.status === "accepted",
    },
    {
      label: "Communication Preferences",
      action: "commPref",
      icon: <ChatAlt2Icon className="w-4 h-4" />,
      show: user.status === "accepted",
    },
  ].filter((item) => item.show);

  return (
    <>
      <div
        className={`bg-white border rounded-lg shadow-sm transition-all duration-200 ${
          selected
            ? "border-primary-light ring-1 shadow-lg shadow-primary ring-primary"
            : "border-gray-200"
        } w-full mb-4`}
      >
        {/* Card Header */}
        <div
          className={`cursor-pointer rounded-t-lg shadow-sm hover:shadow-md transition-shadow duration-200 ${
            isExpanded ? "bg-gray-50" : "bg-white"
          }`}
          onClick={toggleExpanded}
          role="button"
          aria-expanded={isExpanded}
          aria-controls={`user-details-${user._id}`}
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && toggleExpanded()}
        >
          <div className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 flex-wrap">
              {/* Left side - User info */}
              <div
                className="flex items-center gap-3 min-w-0"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Checkbox */}
                <div className="flex items-center justify-center w-8 h-8">
                  <Checkbox
                    label=""
                    name={`select-${user._id}`}
                    checked={selected}
                    onChange={() => handleSelectUser(user._id)}
                    className="h-5 w-5"
                  />
                </div>

                {/* User details */}
                <div className="flex flex-col min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-bold text-gray-900 truncate max-w-xs">
                      {user.name}
                    </h2>

                    <Badge
                      variant={userStatusVariants[user.status] || "default"}
                      className="text-xs font-medium py-1"
                    >
                      {statusMessages[user.status] || user.status}
                    </Badge>

                    {quickStats.membershipTier !== "REGULAR" && (
                      <Badge variant="warning" size="sm">
                        <StarIcon className="w-3 h-3 inline mr-1" />
                        {quickStats.membershipTier}
                      </Badge>
                    )}
                  </div>

                  <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                    <MailIcon className="w-3.5 h-3.5 text-gray-500" />
                    <span className="truncate max-w-xs">{user.email}</span>
                  </div>

                  <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                    <CalendarIcon className="w-3.5 h-3.5 text-gray-500" />
                    <span>Created: {formattedDate}</span>
                  </div>
                </div>
              </div>

              {/* Middle section - Quick Stats */}
              {showAnalytics && quickStats.appointments > 0 && (
                <div
                  className="hidden lg:flex items-center gap-4 px-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-center">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <CalendarIcon className="w-3 h-3" />
                      Appointments
                    </div>
                    <div className="text-lg font-bold">
                      {quickStats.appointments}
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <CheckCircleIcon className="w-3 h-3" />
                      Show Rate
                    </div>
                    <div
                      className={`text-lg font-bold ${
                        quickStats.showRate >= 80
                          ? "text-green-600"
                          : quickStats.showRate >= 50
                          ? "text-yellow-600"
                          : "text-red-600"
                      }`}
                    >
                      {quickStats.showRate}%
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <TrendingUpIcon className="w-3 h-3" />
                      Total Spent
                    </div>
                    <div className="text-lg font-bold text-green-600">
                      ₹{(quickStats.totalSpent / 1000).toFixed(0)}k
                    </div>
                  </div>
                </div>
              )}

              {/* Expansion indicator */}
              <div
                className="order-last sm:order-none flex items-center justify-center sm:mx-2 my-1 sm:my-0"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded();
                }}
              >
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors ${
                    isExpanded ? "bg-gray-100" : ""
                  } text-sm text-gray-600`}
                >
                  <div
                    className={`w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 transition-transform duration-300 ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  >
                    <ChevronDownIcon className="w-3.5 h-3.5" />
                  </div>
                  <span className="font-medium whitespace-nowrap">
                    {isExpanded ? "Hide details" : "Show details"}
                  </span>

                  <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden hidden sm:block">
                    <div
                      className={`h-full bg-primary transition-all duration-300 ease-in-out ${
                        isExpanded ? "w-full" : "w-0"
                      }`}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Right side - Contact and actions */}
              <div
                className="flex flex-wrap items-center gap-2 justify-start sm:justify-end"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Phone pill */}
                <div className="flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 rounded-full text-sm font-medium text-gray-700">
                  <PhoneIcon className="w-3.5 h-3.5 text-gray-500" />
                  <span>{user.phoneNumber}</span>
                </div>

                {/* Quick Actions Dropdown */}
                {quickActionsMenu.length > 0 && (
                  <div className="relative">
                    <button
                      onClick={() => setShowQuickActions(!showQuickActions)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="More actions"
                    >
                      <DotsVerticalIcon className="w-5 h-5 text-gray-600" />
                    </button>

                    {showQuickActions && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setShowQuickActions(false)}
                        />
                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                          <div className="py-1">
                            {quickActionsMenu.map((item, index) => (
                              <button
                                key={index}
                                onClick={() => handleAction(item.action)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors"
                              >
                                <span className="text-gray-500">
                                  {item.icon}
                                </span>
                                <span className="text-gray-700">
                                  {item.label}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                <div className="ml-1">
                  <ActionButtons
                    buttonsConfig={getButtonsConfig()}
                    onAction={handleAction}
                    size="sm"
                    className="space-x-1"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible content */}
        <div
          id={`user-details-${user._id}`}
          className={`transition-all rounded-t-lg duration-300 ease-in-out overflow-hidden ${
            isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="p-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div>
                <div className="flex items-center gap-2 mb-3 pb-2">
                  <UserIcon className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold text-gray-800">
                    Contact Information
                  </h3>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <MailIcon className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-gray-500 text-xs">Email</p>
                      <p className="font-medium text-gray-800">{user.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <PhoneIcon className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-gray-500 text-xs">Phone</p>
                      <p className="font-medium text-gray-800">
                        {user.phoneNumber}
                      </p>
                    </div>
                  </div>

                  {user.address && (
                    <div className="flex items-start gap-2">
                      <LocationMarkerIcon className="w-4 h-4 text-gray-500 mt-0.5" />
                      <div>
                        <p className="text-gray-500 text-xs">Address</p>
                        <p className="font-medium text-gray-800">
                          {user.address.line1}
                          {user.address.line2 ? `, ${user.address.line2}` : ""}
                          <br />
                          {user.address.city}, {user.address.state} -{" "}
                          {user.address.pinCode}
                        </p>
                      </div>
                    </div>
                  )}

                  {user.gstin && (
                    <div className="flex items-center gap-2">
                      <OfficeBuildingIcon className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-gray-500 text-xs">GSTIN</p>
                        <p className="font-medium text-gray-800">
                          {user.gstin}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Admin Message or Quick Analytics */}
              <div>
                {hasAdminMessage ? (
                  <>
                    <div className="flex items-center gap-2 mb-3 pb-2">
                      <ChatAlt2Icon className="w-4 h-4 text-primary" />
                      <h3 className="font-semibold text-gray-800">
                        Admin Message
                      </h3>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="p-3 bg-white rounded border border-gray-200 mb-3 min-h-[80px]">
                        {user.adminMessage || "No message provided"}
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onEditAdminMessage(user)}
                        className="w-full sm:w-auto"
                      >
                        Edit Message
                      </Button>
                    </div>
                  </>
                ) : showAnalytics && quickStats.appointments > 0 ? (
                  <>
                    <div className="flex items-center gap-2 mb-3 pb-2">
                      <ChartBarIcon className="w-4 h-4 text-primary" />
                      <h3 className="font-semibold text-gray-800">
                        Quick Analytics
                      </h3>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white p-3 rounded border border-gray-200">
                          <p className="text-xs text-gray-500">Appointments</p>
                          <p className="text-xl font-bold">
                            {quickStats.appointments}
                          </p>
                        </div>
                        <div className="bg-white p-3 rounded border border-gray-200">
                          <p className="text-xs text-gray-500">Show Rate</p>
                          <p
                            className={`text-xl font-bold ${
                              quickStats.showRate >= 80
                                ? "text-green-600"
                                : "text-yellow-600"
                            }`}
                          >
                            {quickStats.showRate}%
                          </p>
                        </div>
                        <div className="bg-white p-3 rounded border border-gray-200">
                          <p className="text-xs text-gray-500">Total Spent</p>
                          <p className="text-xl font-bold text-green-600">
                            ₹{(quickStats.totalSpent / 1000).toFixed(1)}k
                          </p>
                        </div>
                        <div className="bg-white p-3 rounded border border-gray-200">
                          <p className="text-xs text-gray-500">Loyalty</p>
                          <p className="text-xl font-bold text-yellow-600">
                            {quickStats.loyaltyPoints}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="info-outline"
                        size="sm"
                        onClick={() => setIsAnalyticsOpen(true)}
                        className="w-full"
                      >
                        <ChartBarIcon className="w-4 h-4 mr-2" />
                        View Full Analytics
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-3 pb-2">
                      <InformationCircleIcon className="w-4 h-4 text-primary" />
                      <h3 className="font-semibold text-gray-800">
                        Account Information
                      </h3>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                      <div>
                        <p className="text-gray-500 text-xs">Created On</p>
                        <p className="font-medium text-gray-800">
                          {formattedDate}
                        </p>
                      </div>

                      <div>
                        <p className="text-gray-500 text-xs">Account Type</p>
                        <p className="font-medium text-gray-800">
                          {user.createdByAdmin
                            ? "Admin Created"
                            : "Self Registered"}
                        </p>
                      </div>

                      <div>
                        <p className="text-gray-500 text-xs">Status</p>
                        <Badge
                          variant={userStatusVariants[user.status] || "default"}
                          className="mt-1"
                        >
                          {statusMessages[user.status] || user.status}
                        </Badge>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Summary (visible when collapsed) */}
        <div
          className={`bg-gray-50 grid grid-cols-1 rounded-b-lg sm:grid-cols-3 transition-all duration-300 ${
            isExpanded
              ? "max-h-0 p-0 gap-0 overflow-hidden border-t-0 opacity-0 invisible"
              : "max-h-[200px] p-3 gap-3 opacity-100 visible border-t border-gray-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <div className="bg-primary-light/20 p-2 rounded-full">
              <UserIcon className="w-4 h-4 text-primary" />
            </div>
            <div className="text-sm">
              <p className="text-gray-500">Contact</p>
              <p className="font-medium truncate">
                {user.email || user.phoneNumber}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-primary-light/20 p-2 rounded-full">
              <LocationMarkerIcon className="w-4 h-4 text-primary" />
            </div>
            <div className="text-sm">
              <p className="text-gray-500">Address</p>
              <p className="font-medium truncate">
                {user.address?.city}, {user.address?.state}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-primary-light/20 p-2 rounded-full">
              {showAnalytics && quickStats.appointments > 0 ? (
                <ChartBarIcon className="w-4 h-4 text-primary" />
              ) : (
                <DocumentTextIcon className="w-4 h-4 text-primary" />
              )}
            </div>
            <div className="text-sm">
              <p className="text-gray-500">
                {showAnalytics && quickStats.appointments > 0
                  ? "Analytics"
                  : "Account"}
              </p>
              <p className="font-medium">
                {showAnalytics && quickStats.appointments > 0
                  ? `${quickStats.appointments} appts • ${quickStats.showRate}%`
                  : user.createdByAdmin
                  ? "Admin Created"
                  : "Self Registered"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* All Modals */}
      <UserModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        user={memoizedUser}
        onSubmit={handleEditUser}
        isLoading={loading}
        error={error}
        mode="edit"
      />

      <UserAnalyticsDashboard
        isOpen={isAnalyticsOpen}
        onClose={() => setIsAnalyticsOpen(false)}
        userId={user._id}
        userName={user.name}
      />

      <CustomerProfileManager
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        userId={user._id}
        userName={user.name}
      />

      <LoyaltyManager
        isOpen={isLoyaltyOpen}
        onClose={() => setIsLoyaltyOpen(false)}
        userId={user._id}
        userName={user.name}
      />

      <UserBranchManager
        isOpen={isBranchOpen}
        onClose={() => setIsBranchOpen(false)}
        userId={user._id}
        userName={user.name}
      />

      <AppointmentPreferencesModal
        isOpen={isApptPrefOpen}
        onClose={() => setIsApptPrefOpen(false)}
        userId={user._id}
        user={user}
      />

      <CommunicationPreferencesModal
        isOpen={isCommPrefOpen}
        onClose={() => setIsCommPrefOpen(false)}
        userId={user._id}
        user={user}
      />
    </>
  );
};

export default memo(UserCardEnhanced);
